import type { FirebaseRepository } from '../models/FirebaseRepository'
import { Cubit } from './core'
import type { StaticModelCollection } from '../types'
import { SlideRubric } from '../models/SlideRubric'
import { type MeetingCubit } from './MeetingCubit'
import { RoomStateEngagement } from '../models/RoomStateEngagement'
import {
  type IObservableArray,
  action,
  computed,
  makeObservable,
  observable,
  type ObservableMap,
  when,
  runInAction,
} from 'mobx'
import { SlideQuestionType } from '../models/SlideQuestionType'
import { similarity } from '../util/arrays'
import {
  calculateScalarRawScoreDataForUser,
  calculateMedianEngagementDuration,
  getCoefficientOfVariation,
  getQuizAnswerScore,
  isMeetingQuestionScorable,
} from '../sources-of-truth/scoring'
import { getRoomStateEngagementStreamForRoomState } from '../firestore/RoomStateEngagement'
import { RoomStateRubricResult } from '../models/RoomStateRubricResult'
import {
  getRoomStateRubricResultsForUser,
  getRoomStateRubricResultStreamForAssignment,
} from '../firestore/RoomStateRubricResult'
import { RoomStateRubricResultDetail } from '../models/RoomStateRubricResultDetail'
import {
  getRoomStateRubricResultDetailsForAssignment,
  getRoomStateRubricResultDetailsForRoom,
} from '../firestore/RoomStateRubricResultDetail'
import { submitRubricFeedback } from '../firestore/RoomStateRubricResultFeedback'
import { fetchSectionAssignment } from '../firestore/SectionAssignment'
import { PromptRubricType } from '../models/PromptRubricType'
import { getRoomPromptTypes } from '../firestore/PromptRubricType'
import { collection, orderBy, query } from 'firebase/firestore'
import { collectionSnapshots } from '../firestore-mobx/stream'
import { DateTime } from 'luxon'
import { sortRubrics, sortRubricResults } from '../sources-of-truth/sortRubrics'
import { PublicUser } from '../models/PublicUser'
import { SectionAssignment } from '../models/SectionAssignment'
import {
  getAssignmentRubricsStream,
  type FirestoreAssignmentRubric,
} from '../firestore/AssignmentRubric'
import { AssignmentRubric } from '../models/AssignmentRubric'
import { ServerIdentitiesIds } from '../util/identities'

export class MeetingResultsCubit extends Cubit {
  repository: FirebaseRepository
  slideDeckId: string
  roomStateId: string
  inDialog: boolean
  onStudentAssignmentPage: boolean

  @observable userId: string

  rubrics: StaticModelCollection<SlideRubric>
  rubricTypes: StaticModelCollection<PromptRubricType>
  meetingCubit: MeetingCubit

  rubricResultDetails: StaticModelCollection<RoomStateRubricResultDetail>
  protected _didSeedData: boolean = false
  protected _roomStateEngagement: StaticModelCollection<RoomStateEngagement>
  protected _aiStatusResultsSorted: IObservableArray<
    'pending' | 'success' | 'failure'
  >

  protected _rubricResultsByUser: ObservableMap<
    string,
    StaticModelCollection<RoomStateRubricResult>
  >

  private _assignment: SectionAssignment
  assignmentRubrics: StaticModelCollection<AssignmentRubric>

  constructor(
    repository: FirebaseRepository,
    params: {
      roomStateId: string
      slideDeckId: string
      meetingCubit: MeetingCubit
      userId: string
      inDialog: boolean
      /**
       * if seed data is provided it will populate the respective
       * cubit models and skip fetching via streams
       */
      seedData?: {
        roomStateEngagement: RoomStateEngagement[]
        rubricResultDetails: RoomStateRubricResultDetail[]
        rubricResults: RoomStateRubricResult[]
        rubrics: SlideRubric[]
      }
      onStudentAssignmentPage?: boolean
    }
  ) {
    super()
    this.inDialog = params.inDialog
    this.repository = repository
    this.roomStateId = params.roomStateId
    this.slideDeckId = params.slideDeckId
    this.meetingCubit = params.meetingCubit
    this.rubrics = SlideRubric.emptyCollection(repository)
    this.rubricTypes = PromptRubricType.emptyCollection(repository)
    this.userId = params.userId
    this._aiStatusResultsSorted = observable.array([])
    this._rubricResultsByUser = observable.map()
    this._roomStateEngagement = RoomStateEngagement.emptyCollection(repository)
    this._assignment = SectionAssignment.empty(repository)
    this.assignmentRubrics = AssignmentRubric.emptyCollection(repository)
    this.rubricResultDetails =
      RoomStateRubricResultDetail.emptyCollection(repository)
    this.onStudentAssignmentPage = params.onStudentAssignmentPage ?? false

    if (params.seedData) {
      this._didSeedData = true
      this._roomStateEngagement.replaceModels(
        params.seedData.roomStateEngagement
      )
      this.rubricResultDetails.replaceModels(
        params.seedData.rubricResultDetails
      )
      this.rubrics.replaceModels(params.seedData.rubrics)

      // assume success on ai status
      this._aiStatusResultsSorted.replace(['success'])

      const rubricResultsPerUser = params.seedData.rubricResults.reduce(
        (acc, result) => {
          const { userId } = result.data
          if (!acc.has(userId)) {
            acc.set(userId, [])
          }
          acc.get(userId)?.push(result)
          return acc
        },
        new Map<string, RoomStateRubricResult[]>()
      )

      rubricResultsPerUser.forEach((results, userId) => {
        const collection = RoomStateRubricResult.emptyCollection(repository)
        const sortedResults = sortRubricResults(
          results,
          this.rubrics.models,
          this.meetingCubit.slideDeckSlides.models
        )
        collection.replaceModels(sortedResults)
        this._rubricResultsByUser.set(userId, collection)
      })
    }

    makeObservable(this)

    // Initialize assignment fetching based on preview mode
    this.initializeAssignment()

    // Load the custom rubric config unconditionally — including seed mode, which
    // skips initialize(). The results dialog (instructor "Participant Results"
    // and student "View Summary") always seeds, so without this the displayed AI
    // assessment score would ignore custom weights/score mapping and diverge from
    // the gradebook.
    this.startAssignmentRubricsStream()
  }

  // Custom rubric config (weights + score mapping) drives the displayed AI
  // assessment score, for both students (own results) and faculty. Gated on
  // roomState so section/assignment ids are available; a no-op when they aren't
  // (e.g. demo/preview without a real assignment).
  private startAssignmentRubricsStream(): void {
    when(
      () => this.meetingCubit.roomState.isLoaded,
      () => {
        const { sectionId, assignmentId } = this.meetingCubit.roomState.data
        if (sectionId && assignmentId && !this.isDisposed) {
          this.addStream(
            getAssignmentRubricsStream(this.repository, {
              sectionId,
              assignmentId,
            }),
            (rubrics) => {
              this.assignmentRubrics.replaceModels(rubrics)
            }
          )
        }
      }
    )
  }

  initialize(): void {
    if (this._didSeedData) return

    // Rubrics are fetched server-side via the getSlideDeckForAssignment API
    // and stored on meetingCubit.apiRubrics. Wait for that to load, then
    // populate. This avoids a direct Firestore read that is blocked for
    // codeless-access students.
    when(
      () => this.meetingCubit.apiRubrics.isLoaded,
      () => {
        if (this.isDisposed) return
        const sortedRubrics = sortRubrics(
          this.meetingCubit.apiRubrics.models,
          this.meetingCubit.slideDeckSlides.models
        )
        this.rubrics.replaceModels(sortedRubrics)
      }
    )

    this.addStream(getRoomPromptTypes(this.repository), (rubricTypes) => {
      this.rubricTypes.replaceModels(rubricTypes)
    })

    this.initRubricsResultsForUserStream()

    this.addStream(
      getRoomStateEngagementStreamForRoomState(this.repository, {
        roomId: this.meetingCubit.roomId,
      }),
      (engagement) => {
        this._roomStateEngagement.replaceModels(engagement)
      }
    )

    this.addStream(this.getAIStatusStream(), (aiStatusResultsSorted) => {
      runInAction(() => {
        this._aiStatusResultsSorted.replace(aiStatusResultsSorted)
      })
    })

    // ensure roomState load before starting rubric results streams
    when(
      () => this.meetingCubit.roomState.isLoaded,
      () => {
        const { sectionId, assignmentId } = this.meetingCubit.roomState.data

        if (this.currentUserCanFetchRubricResultsByRoom)
          return this.addStream(
            getRoomStateRubricResultDetailsForRoom(this.repository, {
              roomId: this.roomStateId,
            }),
            (rubricResultDetails) => {
              this.rubricResultDetails.replaceModels(rubricResultDetails)
            }
          )

        // happy path means we should NEVER hit this, but we should know if we do via sentry
        if (!sectionId || !assignmentId)
          throw new Error(
            `missing assignment/section data on roomState: sectionId=${sectionId} assignmentId=${assignmentId}`
          )

        // don't init stream on old cubit
        if (this.isDisposed) return

        // from this point forward if we are viewing results without seed data for a room that
        // we are not a part of, let's assume we are faculty for this section (ta/instructor)

        // the perms for faculty are such that we must load all rubric results for the assignment
        // so we must over-fetch then filter on the client
        this.addStream(
          getRoomStateRubricResultDetailsForAssignment(this.repository, {
            assignmentId,
            sectionId,
          }),
          (rubricResultDetails) => {
            const filtered = rubricResultDetails.filter(
              (d) => d.data.roomId === this.roomStateId
            )
            this.rubricResultDetails.replaceModels(filtered)
          }
        )
        this.addStream(
          getRoomStateRubricResultStreamForAssignment(this.repository, {
            sectionId,
            assignmentId,
          }),
          (rubricResults) => {
            // builds answers for each user to replace in map
            const rubricResultsPerUser = rubricResults.reduce((acc, result) => {
              const { userId } = result.data
              if (!acc.has(userId)) {
                acc.set(userId, [])
              }
              acc.get(userId)?.push(result)
              return acc
            }, new Map<string, RoomStateRubricResult[]>())

            // replace in map
            rubricResultsPerUser.forEach((results, userId) => {
              const filtered = results.filter(
                (r) => r.data.roomId === this.roomStateId
              )
              const collection = RoomStateRubricResult.emptyCollection(
                this.repository
              )
              const sortedResults = sortRubricResults(
                filtered,
                this.rubrics.models,
                this.meetingCubit.slideDeckSlides.models
              )
              collection.replaceModels(sortedResults)
              this._rubricResultsByUser.set(userId, collection)
            })
          }
        )
      }
    )
  }

  @computed
  get individualEvaluationResultsForCurrentUser() {
    // get the role of the current selected user in cubit
    const roles = this.meetingCubit.roomState.data.assignmentRoles ?? {}
    const userRole = this.userId in roles ? roles[this.userId] : null

    // demo evaluation rubrics score the rep, not the session — they belong to
    // the instructor's demos dashboard and are never shown in the meeting
    const presenterRoleIds = new Set(
      this.meetingCubit.roles.models
        .filter((role) => role.isPresenter)
        .map((role) => role.id)
    )

    const resultMap = new Map<string, RoomStateRubricResult>()
    this.rubricResultsForCurrentUser.models.forEach((result) => {
      resultMap.set(result.rubricId, result)
    })
    // now iterate over rubrics, and return results in order of rubrics
    return (
      this.rubrics.models
        .map((rubric) => {
          const validRoles = rubric.data.assignmentRoles ?? []
          const { isPreview } = this.meetingCubit

          if (validRoles.some((roleId) => presenterRoleIds.has(roleId)))
            return null

          // if roles defined, user must have role for it to be applicable (unless preview)
          if (
            !isPreview &&
            validRoles.length > 0 &&
            (!userRole || !validRoles.includes(userRole))
          )
            return null

          const result = resultMap.get(rubric.id)

          if (result) return [rubric, result]

          return null
        })
        // filter null, keep typescript happy with guard cheesing
        .filter(
          (tuple): tuple is [SlideRubric, RoomStateRubricResult] =>
            tuple !== null
        )
    )
  }

  @computed
  get rubricResultsForCurrentUser() {
    return (
      this._rubricResultsByUser.get(this.userId) ??
      RoomStateRubricResult.emptyCollection(this.repository)
    )
  }

  @computed
  get showFeedbackForm() {
    if (!this.meetingCubit.sectionId && !this.meetingCubit.assignmentId) {
      return false
    }

    const inRealMeeting =
      !this.meetingCubit.isPreview && !this.meetingCubit.useMockResults
    if (inRealMeeting) return true

    if (this.onStudentAssignmentPage) {
      const endDate = this.meetingCubit.assignment.expiresAtDate
      if (endDate) {
        // if we are 2 weeks past the end date, do not show feedback form
        const twoWeeksAfterEndDate = endDate.plus({ weeks: 2 }).toJSDate()

        if (Date.now() > twoWeeksAfterEndDate.getTime()) {
          return false
        }

        return true
      }
      return false
    }
  }

  logEvent(name: string, params?: Record<string, unknown>) {
    this.repository.logEvent(name, {
      room_id: this.roomStateId,
      user_id: this.userId,
      slide_deck_id: this.slideDeckId,
      section_id: this.meetingCubit.sectionId,
      assignment_id: this.meetingCubit.assignmentId,
      ...params,
    })
  }

  private initRubricsResultsForUserStream() {
    const uid = this.userId
    const name = `rubricResultsForUser-${uid}`

    // wait for roomState load to determine if we are room member
    when(
      () => this.meetingCubit.roomState.isLoaded,
      () => {
        // if seeded data we don't need to fetch here
        if (
          this._didSeedData ||
          !this.currentUserCanFetchRubricResultsByRoom ||
          this.isDisposed
        )
          return

        if (!this.hasStream(name)) {
          this.addStream(
            getRoomStateRubricResultsForUser(this.repository, {
              roomStateId: this.roomStateId,
              userId: this.userId,
            }),
            (rubricResults) => {
              const results = RoomStateRubricResult.emptyCollection(
                this.repository
              )
              const sortedResults = sortRubricResults(
                rubricResults,
                this.rubrics.models,
                this.meetingCubit.slideDeckSlides.models
              )
              results.replaceModels(sortedResults)
              this._rubricResultsByUser.set(uid, results)
            },
            { name }
          )
        }
      }
    )
  }

  @action
  submitFeedback(rubricId: string, feedback: string) {
    return submitRubricFeedback(this.repository, {
      rubricId,
      feedback,
      roomId: this.roomStateId,
      userId: this.userId,
      sectionId: this.meetingCubit.sectionId!,
      assignmentId: this.meetingCubit.assignmentId!,
    })
  }

  @action
  changeUserId(userId: string) {
    this.userId = userId
    this.initRubricsResultsForUserStream()
  }

  private async initializeAssignment() {
    const { sectionId, assignmentId, isPreview } = this.meetingCubit
    if (!sectionId || !assignmentId || !isPreview) return
    const assignment = await fetchSectionAssignment(this.repository, {
      sectionId,
      assignmentId,
    })

    this._assignment.replaceModel(assignment)
  }

  @computed
  get hasRubrics(): boolean {
    return this.rubrics.models.length > 0
  }

  @computed
  get rubricsById() {
    return this.rubrics.models.reduce(
      (acc, rubric) => {
        acc[rubric.id] = rubric
        return acc
      },
      {} as Record<string, SlideRubric>
    )
  }

  @computed
  get rubricResultsByRubricId() {
    return sortRubricResults(
      this.rubricResultsForCurrentUser.models,
      this.rubrics.models,
      this.meetingCubit.slideDeckSlides.models
    ).reduce(
      (acc, rubricResult) => {
        acc[rubricResult.rubricId] = rubricResult
        return acc
      },
      {} as Record<string, RoomStateRubricResult>
    )
  }

  @computed
  get engagementStatus() {
    const hasEngagement = this._roomStateEngagement.models.length > 0

    // if we have all the engagement data return success regardless of status
    if (hasEngagement && !this.meetingCubit.usersLoading) return 'success'

    if (
      this._roomStateEngagement.isLoading ||
      this.aiStatus === 'pending' ||
      this.meetingCubit.usersLoading
    )
      return 'pending'

    if (!hasEngagement) return 'failure'
  }

  @computed
  get aiStatus() {
    // If we have a success, return success.
    if (this._aiStatusResultsSorted.includes('success')) {
      return 'success'
    }

    if (this._aiStatusResultsSorted.includes('failure')) {
      return 'failure'
    }

    return 'pending'
  }

  @computed
  get engagementByUserId() {
    const hiddenUserIds = new Set(this.meetingCubit.hiddenUserIds)
    return {
      data: this._roomStateEngagement.models.reduce(
        (acc, engagement) => {
          // skip hidden users
          if (hiddenUserIds.has(engagement.id)) return acc
          acc[engagement.data.userId] = engagement
          return acc
        },
        {} as Record<string, RoomStateEngagement>
      ),
      isLoading: this._roomStateEngagement.isLoading,
      hasDocuments: this._roomStateEngagement.hasDocuments,
    }
  }

  @computed
  get userEngagementData() {
    return this.userId in this.engagementByUserId.data
      ? this.engagementByUserId.data[this.userId]
      : RoomStateEngagement.empty(this.repository)
  }

  @computed
  get currentUserCanFetchRubricResultsByRoom() {
    return (
      this.meetingCubit.roomState.userIds.includes(this.repository.uid) ||
      this.repository.breakoutUser?.isCorre === true
    )
  }

  @computed
  get sessionResults() {
    /// loop over state.slideQuestions and get questions
    /// if it is a poll type question
    /// loop over state.roomStateAnswersByQuestion and get answers
    /// extract an array of bools from the from questions that are of
    /// type poll. 1 = true, 0 = false
    /// pass the list to the similarity function
    /// add the results from each question to a double and then divide
    /// by the number of questions and set that to pollAgreement
    /// calculate the quizPerformance as well
    /// (simple average of correct answers)
    let pollTotal = 0.0
    let pollCount = 0

    let groupScore = 0
    let highestPossibleGroupScore = 0

    for (const question of this.meetingCubit.questions) {
      const isPollQuestion = [
        SlideQuestionType.poll,
        SlideQuestionType.customPoll,
      ].includes(question.questionType)

      if (isPollQuestion) {
        const answers = this.meetingCubit.questionAnswers.get(question.id) ?? []
        const answerList = answers.map((answer) => answer.data.answer === 1)
        pollTotal += similarity(answerList)
        pollCount++
      }

      if (isMeetingQuestionScorable(question)) {
        const answers = this.meetingCubit.questionAnswers.get(question.id) ?? []
        for (const answer of answers) {
          groupScore += getQuizAnswerScore({
            answer,
            question: question.slideQuestion,
          })
          highestPossibleGroupScore++
        }
      }
    }

    const pollAgreement =
      Math.round((pollTotal / Math.max(pollCount, 1)) * 100) / 100
    const quizPerformance =
      Math.round((groupScore / Math.max(highestPossibleGroupScore, 1)) * 100) /
      100

    const engagementDataArr = this._roomStateEngagement.models
    const talkTimeMinutes =
      (this.userEngagementData.data.userTalkTime || 0) / 60
    const groupAverageTalkTimeMinutes =
      engagementDataArr.reduce(
        (acc, { data: { userTalkTime } }) => acc + (userTalkTime || 0),
        0
      ) /
      60 /
      engagementDataArr.length
    const cameraOnPercent = (this.userEngagementData.cameraOnRatio || 0) * 100

    const groupCameraOnRatios = engagementDataArr
      .map((d) => d.cameraOnRatio)
      .filter((r) => r !== null) as number[]

    const groupAverageCameraOnPercent =
      (groupCameraOnRatios.reduce((total, ratio) => total + ratio, 0) /
        groupCameraOnRatios.length) *
      100

    return {
      isLoaded:
        this._roomStateEngagement.isLoaded &&
        this.meetingCubit.slideDeckQuestions.isLoaded &&
        this.meetingCubit.roomStateAnswersForGroup.isLoaded,
      talkTimeMinutes,
      groupAverageTalkTimeMinutes,
      cameraOnPercent,
      groupAverageCameraOnPercent,
      pollAgreement,
      quizPerformance,
      userQuizPerformance: this._userQuizPerformance,
    }
  }

  @computed
  get roomStateAnswersForUser() {
    return this.meetingCubit.roomStateAnswersForGroup.models.filter(
      (answer) => answer.data.userId === this.meetingCubit.currentUser.uid
    )
  }

  @computed
  get assignment() {
    // In preview mode, use our locally fetched assignment
    // In normal mode, use the meetingCubit's assignment
    return this.meetingCubit.isPreview
      ? this._assignment
      : this.meetingCubit.assignment
  }

  /**
   * Custom rubric config keyed by rubricId, used by scoring to apply per-role
   * weights + scoreMapping. undefined unless the assignment opts in via
   * `usesCustomRubrics`, so scoring falls back to the legacy average otherwise.
   */
  @computed
  get customRubricsById(): Map<string, FirestoreAssignmentRubric> | undefined {
    if (!this.assignment.data.usesCustomRubrics) return undefined
    const map = new Map<string, FirestoreAssignmentRubric>()
    this.assignmentRubrics.models.forEach((rubric) => {
      map.set(rubric.id, rubric.data)
    })
    return map
  }

  /**
   * Get final score data for the user, only if showFinalGradeToParticipants is true
   * Returns null if the feature is disabled or no scalar data is available
   */
  @computed
  get finalScoreData() {
    // Check if assignment is loaded
    if (!this.assignment.isLoaded) {
      return null
    }

    // Only show final score if the assignment has this enabled
    if (!this.assignment.data.showFinalGradeToParticipants) {
      return null
    }

    // Get the user's scalar score data using existing computed property
    const userScoreData = this.scalarRawScoreDataForUser

    if (!userScoreData) return null

    // Only withhold the score while rubric data is pending if the rubric
    // portion actually contributes to it. Otherwise (e.g. quiz/attendance/
    // completion-only assignments) the score is complete from the start.
    const rubricWeight =
      this.assignment.data.assignmentGradingScalars?.totalRubricScore ?? 0
    if (
      rubricWeight > 0 &&
      this.aiStatus !== 'success' &&
      this.rubricResultsForCurrentUser.length === 0
    ) {
      return null
    }

    // Convert user score to percentage
    const userFinalScore = Math.ceil(userScoreData.totalScore * 100)

    return {
      userScore: userFinalScore,
      // No group average due to security concerns, but UI should reserve space
    }
  }

  @computed
  /**
   * Calculate scalar raw score data for the current user
   */
  get scalarRawScoreDataForUser() {
    if (!this.meetingCubit.roomState.isLoaded) return null

    const scalars = this.assignment.data.assignmentGradingScalars

    // Build quiz scores map
    const quizScores = new Map<string, number | null>()
    const userQuizPerformance = this._userQuizPerformance
    quizScores.set(this.userId, userQuizPerformance)

    // Build rubric results map
    const rubricResults = new Map<
      string,
      Map<SlideRubric, RoomStateRubricResult[]>
    >()
    const userRubricCollection = this._rubricResultsByUser.get(this.userId)
    if (userRubricCollection) {
      const userRubricResults = new Map<SlideRubric, RoomStateRubricResult[]>()
      for (const result of userRubricCollection.models) {
        const rubric = this.rubrics.models.find((r) => r.id === result.id)
        if (rubric) {
          if (!userRubricResults.has(rubric)) {
            userRubricResults.set(rubric, [])
          }
          userRubricResults.get(rubric)!.push(result)
        }
      }
      rubricResults.set(this.userId, userRubricResults)
    }

    // Build engagement data map
    const engagementData = new Map<string, RoomStateEngagement>()
    const userEngagement = this._roomStateEngagement.models.find(
      (e) => e.data.userId === this.userId
    )
    if (userEngagement) {
      engagementData.set(this.userId, userEngagement)
    }

    const medianEngagementDuration = calculateMedianEngagementDuration(
      this._roomStateEngagement.models
    )

    return calculateScalarRawScoreDataForUser({
      userId: this.userId,
      scalars,
      quizScores,
      rubricResults,
      rubricTypes: this.rubricTypes.models,
      roomState: this.meetingCubit.roomState,
      engagementData,
      medianEngagementDuration,
      assignmentRubrics: this.customRubricsById,
    })
  }

  @computed
  /**
   * Calculate the user's quiz performance a float between 0 and 1
   * if null there was no quiz answer data for user
   */
  private get _userQuizPerformance() {
    let score = 0
    let quizCount = 0

    for (const question of this.meetingCubit.questions) {
      if (isMeetingQuestionScorable(question)) {
        quizCount++

        const userAnswersMaybe = this.meetingCubit.roomStateAnswersPerUser.get(
          this.userId
        )
        const userAnswers = userAnswersMaybe ? userAnswersMaybe.models : []

        const userAnswer = userAnswers.find(
          (a) => a.data.slideQuestionId === question.id
        )

        if (userAnswer) {
          score =
            score +
            getQuizAnswerScore({
              answer: userAnswer,
              question: question.slideQuestion,
            })
        }
      }
    }

    if (!quizCount) return null
    return Math.round((score / Math.max(quizCount, 1)) * 100) / 100
  }

  @computed
  get conversationMapDataLoading() {
    return this.rubricResultDetails.isLoading
  }

  @computed
  private get timeBoundsForConversationMap(): {
    earliest: Date | undefined | null
    latest: Date | undefined | null
  } {
    // get earliest / latest result detail times
    const data = this.rubricResultDetails.models.reduce<{
      earliest: Date | null
      latest: Date | null
    }>(
      (acc, { data: { eventTime } }) => {
        const { earliest, latest } = acc
        if (!earliest || eventTime < earliest) {
          acc.earliest = eventTime
        }
        if (!latest || eventTime > latest) {
          acc.latest = eventTime
        }
        return acc
      },
      { earliest: null, latest: null }
    )

    // do not continue of no earliest/latest for results
    if (data.earliest === null || data.latest === null) return data

    /** determine if details are in a range of times (not all the same time)*/
    const detailsCoverTimeRange =
      DateTime.fromJSDate(data.latest).diff(DateTime.fromJSDate(data.earliest))
        .milliseconds > 0

    const { roomStartedAt, activeSlideChangedAt } =
      this.meetingCubit.roomState.data

    const activeSlideChangedAtDateTime =
      activeSlideChangedAt && DateTime.fromJSDate(activeSlideChangedAt)
    const roomStartedAtDateTime =
      roomStartedAt && DateTime.fromJSDate(roomStartedAt)

    const earliestDateTime = DateTime.fromJSDate(data.earliest)
    const latestDateTime = DateTime.fromJSDate(data.latest)

    if (activeSlideChangedAtDateTime && roomStartedAtDateTime) {
      // if no time span to render details (all same time or one result), use room started at and active slide changed at
      if (!detailsCoverTimeRange)
        return {
          earliest: roomStartedAt,
          latest: activeSlideChangedAt,
        }

      const meetingLengthMinutes = activeSlideChangedAtDateTime.diff(
        roomStartedAtDateTime,
        'minutes'
      ).minutes

      // if meeting is longer than 90 minutes, do not use earliest/latest details times as the bounds
      if (meetingLengthMinutes > 90) return data

      // if active slide changed at is <10 mins ahead of last event, use it as end time
      if (activeSlideChangedAtDateTime > latestDateTime) {
        const activeSlideChangedAtMinutesAhead = latestDateTime.diff(
          activeSlideChangedAtDateTime,
          'minutes'
        ).minutes

        if (activeSlideChangedAtMinutesAhead < 10)
          data.latest = activeSlideChangedAtDateTime.toJSDate()
      }

      // if room started at is <10 mins behind first event, use it as start time
      if (roomStartedAtDateTime < earliestDateTime) {
        const earliestDateTimeMinutesBehind = earliestDateTime.diff(
          roomStartedAtDateTime,
          'minutes'
        ).minutes
        if (earliestDateTimeMinutesBehind < 10)
          data.earliest = roomStartedAtDateTime.toJSDate()
      }
    }

    return data
  }

  @computed
  /**
   * Computes data for the conversation map visualization by:
   * 1. Getting the time bounds for the conversation
   * 2. Sorting all rubric results by time
   * 3. Dividing the conversation into 5 equal time buckets
   * 4. Distributing the results into the appropriate time buckets
   *
   * @returns Object containing:
   * - startTimeTS: Start timestamp of the conversation
   * - endTimeTS: End timestamp of the conversation
   * - buckets: Array of 5 buckets containing rubric results
   * - minutesPerBucket: Duration of each bucket in minutes
   */
  get conversationMapData() {
    // Get the earliest and latest timestamps for the conversation
    const { earliest, latest } = this.timeBoundsForConversationMap

    if (!earliest || !latest) return undefined

    const timeData = {
      firstEventTime: earliest,
      lastEventTime: latest,
      startTimeTS: earliest.getTime(),
      endTimeTS: latest.getTime(),
      meetingDurationMs: latest.getTime() - earliest.getTime(),
    }

    // Sort all rubric results by timestamp
    const sortedResults = this.rubricResultDetails.models
      .concat()
      // filter out results from server identities as they are not relevant
      .filter((r) => !ServerIdentitiesIds.includes(r.data.userId))
      .sort((a, b) => a.data.eventTime.getTime() - b.data.eventTime.getTime())

    if (sortedResults.length === 0) return undefined

    // Divide conversation into 5 equal time buckets
    const bucketCount = 5
    const bucketSizeMilliseconds = timeData.meetingDurationMs / bucketCount

    // Initialize array of empty buckets
    const buckets: RoomStateRubricResultDetail[][] = Array.from({
      length: bucketCount,
    })

    for (let i = 0; i < bucketCount; i++) {
      buckets[i] = []
    }

    // Distribute results into appropriate time buckets
    for (const rubricResultDetail of sortedResults) {
      const eventTime = rubricResultDetail.data.eventTime.getTime()
      const pointInTime = eventTime - timeData.startTimeTS
      const bucketIndex = Math.floor(
        (pointInTime > 0 ? pointInTime - 1 : pointInTime) /
          bucketSizeMilliseconds
      )

      if (bucketIndex >= 0 && bucketIndex < bucketCount) {
        buckets[bucketIndex].push(rubricResultDetail)
      }
    }

    const startTimeTS = timeData.startTimeTS
    const endTimeTS = timeData.endTimeTS

    return {
      startTimeTS,
      endTimeTS,
      buckets,
      minutesPerBucket: bucketSizeMilliseconds / 1000 / 60,
    }
  }

  generateConversationMapScaleData(
    buckets: RoomStateRubricResultDetail[][],
    realWidth: number
  ) {
    /**
     * low end estimate for packing efficiency of circles to calculate height pessimistically.
     * Perfect packing efficiency is about ~90% for same radius circles packed in a grid.
     * Here we are starting at 65% and will scale **down** if the height is proves to be
     * excessive (circle svg's clipping out of bounds). If need be adjusted scale down in 5% increments
     */
    const pessimisticPackingEfficiencyFactor = 0.65

    let startSize = 30
    let scoreMultiplier = 10
    let parentHeight = 200
    const bucketWidth = 500
    const circleScaleFactor = realWidth / bucketWidth

    // parentHeight - 33 = bucket height
    const bucketLabelHeight = 33
    const getBucketHeight = () => parentHeight - bucketLabelHeight
    const getNewParentHeight = (bucketHeight: number) =>
      bucketHeight + bucketLabelHeight

    let pessimisticBucketHeight = Infinity

    while (pessimisticBucketHeight > getBucketHeight()) {
      const bucketHeight = getBucketHeight()
      const maxCumulativeCircleArea = buckets.reduce((maxSoFar, results) => {
        const sumCircleArea = results.reduce<number>((acc, resultDetail) => {
          const {
            data: { score },
          } = resultDetail
          const radius =
            (startSize + score * scoreMultiplier) * circleScaleFactor
          const area = Math.PI * radius * radius
          return acc + area
        }, 0)
        return Math.max(maxSoFar, sumCircleArea)
      }, 0)

      // use the pessimistic efficiency factor to determine the approximate bucket height
      const heightNoInefficiency = maxCumulativeCircleArea / realWidth
      pessimisticBucketHeight =
        heightNoInefficiency / pessimisticPackingEfficiencyFactor

      // scale min size from 30 to minimum of 10, in 5 pixel increments
      // then scale down score multiplier from 5 to 10
      // once circles reach min size increase bucket height to pessimistic height
      // too accommodate display of all circles
      if (pessimisticBucketHeight > bucketHeight) {
        if (startSize > 15) {
          startSize -= 5
          continue
        }
        if (scoreMultiplier > 5) {
          scoreMultiplier -= 1
          continue
        }
        // if we've exhausted all scaling options, set bucket height to pessimistic and round up to the nearest 10
        parentHeight = getNewParentHeight(
          Math.ceil(pessimisticBucketHeight / 10) * 10
        )
      }
    }

    const data = {
      startSize,
      scoreMultiplier,
      parentHeight,
    }

    return data
  }

  private getAIStatusStream() {
    const colRef = collection(
      this.repository.firestore,
      `room_state/${this.roomStateId}/ai`
    )
    // show latest startedAt first
    const q = query(colRef, orderBy('startedAt', 'desc'))
    return collectionSnapshots(q).map((snapshots) => {
      return snapshots.docs.map((doc) => {
        // Nil state is pending.
        let result: 'pending' | 'success' | 'failure' = 'pending'
        const data = doc.data()

        if ('success' in data && typeof data.success === 'boolean') {
          result = data.success ? 'success' : 'failure'
        }

        return result
      })
    })
  }

  @computed
  get groupBalanceSummary(): GroupBalanceSummary {
    const {
      sessionResults: { groupAverageTalkTimeMinutes },
      engagementByUserId,
    } = this

    const talkTimeMinutesByUserID: Record<string, number> = {}
    let highestTalkTime = 0
    let lowestTalkTime = Infinity

    // generate talk times for each uid
    for (const [userId, { talkTimeMinutes }] of Object.entries(
      engagementByUserId.data
    )) {
      talkTimeMinutesByUserID[userId] = talkTimeMinutes
      if (talkTimeMinutes > highestTalkTime) highestTalkTime = talkTimeMinutes
      if (talkTimeMinutes < lowestTalkTime) lowestTalkTime = talkTimeMinutes
    }

    const graphUserIds = Object.keys(talkTimeMinutesByUserID)

    // get name labels for each user
    // if there are duplicate first names, add last initial
    // if duplicate first names with last initial, show full name
    const seenFirstNames: Set<string> = new Set()
    const seenFirstNamesWithInitial: Set<string> = new Set()
    const duplicateFirstNames: Array<string> = []
    const duplicateFirstNamesWithInitial: Array<string> = []

    const users: Record<
      string,
      { user: PublicUser; firstNameWithInitial: string }
    > = graphUserIds.reduce(
      (
        acc: Record<string, { user: PublicUser; firstNameWithInitial: string }>,
        userId
      ) => {
        const user =
          this.meetingCubit.users.find((u) => u.id === userId) ||
          PublicUser.empty(this.repository)
        const firstName = user.data.firstName.trim()
        const firstNameWithInitial =
          user.data.firstName.trim() +
          (user.data.lastName.trim()
            ? ' ' + user.data.lastName.trim().charAt(0) + '.'
            : '')
        acc[userId] = { user, firstNameWithInitial }
        if (seenFirstNames.has(firstName)) duplicateFirstNames.push(firstName)
        if (seenFirstNamesWithInitial.has(firstNameWithInitial))
          duplicateFirstNamesWithInitial.push(firstNameWithInitial)
        seenFirstNames.add(firstName)
        seenFirstNamesWithInitial.add(firstNameWithInitial)
        return acc
      },
      {}
    )

    // generate record with key UID and value talk time / name label
    const talkTimeAndNameLabel: Array<{
      uid: string
      talkTimeMinutes: number
      talkTimeMinutesRounded: number
      nameLabel: string
    }> = graphUserIds.map((uid) => {
      const nameLabel = (() => {
        const user = users[uid].user || PublicUser.empty(this.repository)
        const firstName = user.data?.firstName || ''
        const firstNameWithInitial = users[uid].firstNameWithInitial ?? ''

        if (
          duplicateFirstNames.includes(firstName) &&
          duplicateFirstNamesWithInitial.includes(firstNameWithInitial)
        ) {
          return user.fullName
        } else {
          return firstNameWithInitial
        }
      })()
      const talkTimeMinutes = talkTimeMinutesByUserID[uid] ?? 0
      const talkTimeMinutesRounded = Math.round(talkTimeMinutes * 10) / 10 // round talk time minutes to 1 decimal
      return {
        uid,
        talkTimeMinutes,
        talkTimeMinutesRounded,
        nameLabel,
      }
    })

    // get coefficient of variation and check against threshold from feature flags to see if discussion was balanced
    // or asymmetric
    const coefficientOfVariation = getCoefficientOfVariation(
      talkTimeAndNameLabel.map((x) => x.talkTimeMinutes)
    )
    const CVthreshold =
      this.repository.featureFlags.data.sessionResultsGroupBalanceCoefficient
    const balanced = coefficientOfVariation < CVthreshold

    // upper bound should be 2x the average unless the highest talk time is higher
    // lower bound should be 0 unless the lowest talk is 0, then lower bound should be negative 1
    // to render bars even if user had 0 talk minutes
    //todo(ashold12): if not dynamic enough use larger of the following median OR average to calc upper bound
    const upperBound = Math.max(
      groupAverageTalkTimeMinutes * 2,
      highestTalkTime
    )
    const lowerBound = lowestTalkTime > 0 ? 0 : -1
    const averageTalkTime = groupAverageTalkTimeMinutes
    const averageTalkTimeRounded = Math.round(averageTalkTime * 10) / 10
    return {
      chartData: talkTimeAndNameLabel,
      averageTalkTime,
      averageTalkTimeRounded,
      upperBound,
      lowerBound,
      balanced,
    }
  }

  @computed
  get hasScorableQuizzes() {
    return this.meetingCubit.questions.some((q) => isMeetingQuestionScorable(q))
  }
}

export type GroupBalanceSummary = {
  balanced: boolean
  averageTalkTime: number
  averageTalkTimeRounded: number
  upperBound: number
  lowerBound: number
  chartData: Array<{
    uid: string
    talkTimeMinutes: number
    talkTimeMinutesRounded: number
    nameLabel: string
  }>
}
